import { create, useModal } from '@ebay/nice-modal-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Api } from 'assets/icons/Api.svg';
import { ReactComponent as Roles } from 'assets/icons/Roles.svg';
import { ReactComponent as Settings } from 'assets/icons/SettingsNew.svg';
import { ReactComponent as SubWorkspaces } from 'assets/icons/SubWorkspaces.svg';
import { ReactComponent as Users } from 'assets/icons/Users.svg';
import useActorWorkspaceRole from 'hooks/useActorWorkspaceRole/useActorWorkspaceRole';
import useUserPowerLevel from 'hooks/useUserPowerLevel';
import SettingsModal, { Tab } from 'modules/SettingsModal/SettingsModal';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import testingConstants from 'utils/testingConstants';

import ApiKeysTab from './ApiKeysTab/ApiKeysTab';
import GeneralTab from './GeneralTab/GeneralTab';
import MembersTab from './MembersTab/MembersTab';
import SubWorkspacesTab from './SubWorkspacesTab/SubWorkspacesTab';
import { shouldShowApiKeysTab, shouldShowWorkspaceRolesTab, WorkspaceSettingsTabs } from './utils';
import WorkspaceRolesSetting from './WorkspaceRolesSetting/WorkspaceRolesSetting';

const WorkspaceSettingsModal = create(() => {
  const { t } = useTranslation();
  const { remove } = useModal(WorkspaceSettingsModal);
  const { workspacePermissions, workspaceId } = useWorkspacesContext({ requireWorkspaceId: true });
  const actorRole = useActorWorkspaceRole();
  const workspacePowerLevel = useUserPowerLevel(workspaceId);

  const tabs = useMemo(() => {
    const visibleTabs: Tab[] = [
      {
        id: WorkspaceSettingsTabs.General,
        title: t('workspaces.general'),
        icon: Settings,
        contentComponent: GeneralTab,
      },
      {
        id: WorkspaceSettingsTabs.Members,
        title: t('workspaces.members'),
        icon: Users,
        contentComponent: MembersTab,
      },
    ];

    if (shouldShowWorkspaceRolesTab(workspacePermissions.adjustRoles, actorRole)) {
      visibleTabs.push({
        id: WorkspaceSettingsTabs.Roles,
        title: t('workspaces.roles'),
        icon: Roles,
        contentComponent: WorkspaceRolesSetting,
      });
    }

    if (shouldShowApiKeysTab(workspacePowerLevel)) {
      visibleTabs.push({
        id: WorkspaceSettingsTabs.ApiKeys,
        title: t('workspaces_settings.api_keys.tab_title'),
        icon: Api,
        contentComponent: ApiKeysTab,
        dataTestId: testingConstants.workspaceSettingsApiKeysTab,
      });
    }

    visibleTabs.push({
      id: WorkspaceSettingsTabs.SubWorkspaces,
      title: t('workspaces.sub_workspaces'),
      icon: SubWorkspaces,
      contentComponent: SubWorkspacesTab,
    });

    return visibleTabs;
  }, [workspacePermissions.adjustRoles, actorRole, workspacePowerLevel, t]);

  return <SettingsModal tabs={tabs} title={t('workspaces.settings')} onClose={remove} />;
});

export default WorkspaceSettingsModal;
